import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {map} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import { RawMaterialDto, RawMaterialFilterDto, RawMaterialService} from '@sharedrest/data.restservice';
import {UserService} from '@sharedcommon/services/user.service';
import {Constants} from '@sharedcommon/util/constants.util';
import {FunctionUtils} from '@sharedcommon/util/functions.util';
import {EntityServiceClient, MatrixDto, MatrixFilterDto} from '@sharedrest/equation.restservice';
import {UserResultPref, UserResultPrefValueType} from '@sharedcommon/model/user.result-pref.model';
import {ResultSettingsService} from '@usercommon/service/result-settings.service';
import {ToastService} from '@sharedcommon/services/toast.service';
import {NeqAdvancedRawmaterialsSearchPopinComponent} from '@usercommon/components/advanced-rawmaterials-search/advanced-rawmaterials-search-popin.component';
import {PrimeTableDataModel} from '@sharedcommon/model/prime-table-data.model';
import {PrimengTableCols} from '@sharedcommon/interfaces/primeng-table-cols';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import { NeqAdvancedSamplesSearchPopinComponent } from '@usercommon/components/advanced-samples-search/advanced-samples-search-popin.component';
import { lastValueFrom } from 'rxjs';
import { SampleFilterDto } from '@sharedcommon/model/sample/filter/sample-filter.model';
import { SampleRestService } from '@sharedcommon/rest/sample/sample.restservice';
import { SampleDashboardDto } from '@sharedcommon/model/sample/sample-dashboard.model';
import { SampleDto } from '@sharedcommon/model/sample/sample.model';

/**
 * Pop-in used to set the preferences on the value to display for tha values component
 * which is in NeqResultsTableComponent
 */
@Component({
  templateUrl: './rawmaterials-or-samples-results-settings-popin.component.html'
})
export class NeqRawmaterialOrSampleResultsSettingsPopin implements OnInit {

  /**
   * Data of the primeNg table
   */
  tableData: PrimeTableDataModel;

  /**
   * Columns of the primeng table
   */
  cols: PrimengTableCols[];

  form: FormGroup;
  matrices: MatrixDto[];
  resultPrefs: UserResultPref[];

  valueTypes = [
    UserResultPrefValueType.CALCULATED,
    UserResultPrefValueType.SAVED,
    UserResultPrefValueType.STANDARD
  ];

  constructor(
    private readonly userService: UserService,
    private readonly rawMatService: RawMaterialService,
    private readonly sampleService: SampleRestService,
    private readonly entityService: EntityServiceClient,
    private readonly resultSettingsService: ResultSettingsService,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly dialogService: MatDialog,
    private readonly dialogRef: MatDialogRef<NeqRawmaterialOrSampleResultsSettingsPopin>
  ) {
  }


  ngOnInit() {
    this.initTableCols();
    this.form = new FormGroup({
      resultShowUnit: new FormControl(false, []),
      resultTolerancePercent: new FormControl(undefined, [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
        Validators.pattern(Constants.REGEX_INTEGER_POSITIVE)
      ])
    });

    // Init form
    this.form.get('resultShowUnit').patchValue(false);
    this.form.get('resultTolerancePercent').patchValue('');
    this.form.get('resultTolerancePercent').markAsTouched();
    this.form.markAsDirty();

    this.userService.getMetas().forEach(meta => {

      switch (meta.key) {
        case Constants.USER_META_RESULT_SHOW_UNIT:
          if (meta.value !== 'false') {
            this.form.get('resultShowUnit').patchValue(false);
          } else {
            this.form.get('resultShowUnit').patchValue(true);
          }
          break;
        case Constants.USER_META_RESULT_TOLERANCE_PERCENT:
          this.form.get('resultTolerancePercent').patchValue(Number(meta.value));
          break;
      }
    });
    // deep clone with JSON :)
    this.resultPrefs = JSON.parse(JSON.stringify(this.resultSettingsService.getResultPrefs()));
    this.refreshPrimeNgTable();
    this.entityService.matricesFetch(this.userService.getEntity(), new MatrixFilterDto()).subscribe(result => {
      this.matrices = result;
    });
  }

  private initTableCols(): void {
    const sampleOrRawMatCol: PrimengTableCols = this.userService.isFeedOperatingMode() ?
                                                    {field: 'rawMaterial', header: 'label.common.rawmaterial'}
                                                    : {field: 'sample', header: 'label.common.sample'}
    this.cols = [
      sampleOrRawMatCol,
      {field: 'matrix', header: 'label.common.matrix'},
      {field: 'valueType', header: 'label.user.rawmaterial.value'}
    ];
  }


  /**
   * Return true for the first line of the table
   *
   * @param index line index
   */
  first(index: number): boolean {
    return index === 0;
  }

  /**
   * Return true if index (of the row) is the last of the list
   *
   * @param index line index
   */
  last(index: number): boolean {
    return (this.tableData?.results && this.tableData.results.length === index + 1);
  }

  /**
   * Refresh data of the primeng table
   */
  refreshPrimeNgTable() {
    const data = new PrimeTableDataModel();
    data.results = this.resultPrefs;
    this.tableData = data;
  }

  isNotCurrentRawMatOrSample(resultPref: UserResultPref) {
    return resultPref?.rawMaterialOrSample && resultPref.rawMaterialOrSample.id !== Constants.USER_RESULT_CURRENT_RAWMATERIAL_ID;
  }

  addRow() {
    if (this.resultPrefs.length >= 15) {
      this.toastService.toast(this.translate.instant('msg.user.rawmaterials.results.settings.add.max'));
      return;
    }
    this.addResultPrefs(this.resultSettingsService.newDefaultResultPref());
  }

  goRmAdvancedSearch(index: number) {


    const dialog = this.dialogService.open(NeqAdvancedRawmaterialsSearchPopinComponent, {
      data: {
        onlyRawMaterial: true
      }
    });

    dialog.afterClosed().subscribe(data => {
      if (data) {
        if (FunctionUtils.isDefined(data)
          && FunctionUtils.isDefined(data['rawMaterial'])
          && FunctionUtils.isDefined(data['rawMaterial'].value)) {
          this.resultPrefs[index].rawMaterialOrSample = this.resultSettingsService.createRawMaterialOrSampleDatasource(data['rawMaterial'].value);
        }
      }
    });
  }

  goSampleAdvancedSearch(index: number) {
    const dialog = this.dialogService.open(NeqAdvancedSamplesSearchPopinComponent, {
      data: {
        singleSelectionMode: true
      }
    });

    dialog.afterClosed().subscribe((data: SampleDto) => {
      if (data) {
        this.resultPrefs[index].rawMaterialOrSample = this.resultSettingsService.createRawMaterialOrSampleDatasource(data);
      }
    });
  }

  hasSeveralMatrices(): boolean {
    return FunctionUtils.isDefined(this.matrices) && this.matrices.length > 1;
  }

  getResultPref(): UserResultPref[] {
    return this.resultPrefs;
  }

  delete(item: UserResultPref) {
    this.resultPrefs = this.resultPrefs.filter(value => {
      return value.id !== item.id;
    });
    this.refreshPrimeNgTable();
  }

  sortUp(index: number) {
    this.resultPrefs = this.moveItemInArrayFromIndexToIndex(this.resultPrefs, index, index - 1);
    this.refreshPrimeNgTable();
  }

  sortDown(index: number) {
    this.resultPrefs = this.moveItemInArrayFromIndexToIndex(this.resultPrefs, index, index + 1);
    this.refreshPrimeNgTable();
  }

  addResultPrefs(resultPref: UserResultPref) {
    let maxId: number;
    if (FunctionUtils.isNotDefined(this.resultPrefs)) {
      maxId = 1;
    } else {
      const maxResultPref = this.resultPrefs.reduce((previousValue, currentValue) => {
        return previousValue.id > currentValue.id ? previousValue : currentValue;
      });
      maxId = maxResultPref.id + 1;
    }
    resultPref.id = maxId;
    resultPref.valueType = UserResultPrefValueType.SAVED;
    this.resultPrefs.push(resultPref);
    this.refreshPrimeNgTable();
  }

  selectedRawMaterialOrSampleChanged(row: UserResultPref) {
    row.valueType = UserResultPrefValueType.SAVED;
  }

  updateResultPrefs(resultPref: UserResultPref) {
    const index = this.resultPrefs.findIndex((obj => obj.id === resultPref.id));
    if (index !== -1) {
      this.resultPrefs[index] = resultPref;
    }
  }

  rawMaterialsDatasource = (searchTerm, page) => {
    const filter = new RawMaterialFilterDto();

    if (searchTerm) {
      filter.matchAny = true;
      filter.code = searchTerm;
      filter.label = searchTerm;
    }

    filter.page = page;
    filter.count = 10;
    filter.sort = [];

    return lastValueFrom(this.rawMatService.fetch(this.userService.getEntity(), filter)
      .pipe(
        map(
          result => {
            if (page === 1) {
              // add default current raw material
              result.data.unshift(this.resultSettingsService.createDefaultRawMaterialOrSample() as RawMaterialDto);
            }
            return result;
          }
        ),
        map(result => result.data.map(rawMaterial => {
          return this.resultSettingsService.createRawMaterialOrSampleDatasource(rawMaterial);
        }))
      ));
  };

  samplesDatasource = (searchTerm, page) => {
    const filter = new SampleFilterDto();

    if (searchTerm) {
      filter.matchAny = true;
      filter.code = searchTerm;
      filter.label = searchTerm;
    }

    filter.page = page;
    filter.count = 10;
    filter.sort = [];

    return lastValueFrom(this.sampleService.searchSampleDashboardByEntityCode(this.userService.getEntity(), filter, true)
      .pipe(
        map(
          result => {
            if (page === 1) {
              // add default current raw material
              result.data.unshift(this.resultSettingsService.createDefaultRawMaterialOrSample() as SampleDashboardDto);
            }
            return result;
          }
        ),
        map(result => result.data.map(rawMaterial => {
          return this.resultSettingsService.createRawMaterialOrSampleDatasource(rawMaterial);
        }))
      ));
  };

  matricesDatasource = (searchTerm, page) => {
    const filter = new MatrixFilterDto();

    if (searchTerm) {
      filter.matchAny = true;
      filter.code = searchTerm;
      filter.label = searchTerm;
    }

    filter.page = page;
    filter.count = 10;
    filter.sort = [];

    return lastValueFrom(
      this.entityService.matricesFetch(this.userService.getEntity(), filter)
        .pipe(
          map(result =>
            result.map(matrix => {
              return this.resultSettingsService.createMatrixDatasource(matrix);
            })
          )
        ));
  };

  continue() {
    if (this.form.invalid) {
      return;
    }

    const notValidPref = this.resultPrefs.filter(value => FunctionUtils.isNotDefined(value.matrix) ||
      FunctionUtils.isNotDefined(value.rawMaterialOrSample) || FunctionUtils.isNotDefined(value.valueType));

    if (this.resultPrefs.length === 0 || notValidPref.length > 0) {
      this.toastService.toast(this.translate.instant('error.user.rawmaterials.results.settings.invalid'));
      return;
    }

    this.resultSettingsService.setResultPrefs(this.resultPrefs);

    const newShowUnitValue = String(!this.form.get('resultShowUnit').value);
    this.userService.saveMeta(Constants.USER_META_RESULT_SHOW_UNIT, newShowUnitValue);

    if (FunctionUtils.isDefined(this.form.get('resultTolerancePercent').value)) {
      this.userService.saveMeta(Constants.USER_META_RESULT_TOLERANCE_PERCENT, this.form.get('resultTolerancePercent').value);
    } else {
      this.userService.saveMeta(Constants.USER_META_RESULT_TOLERANCE_PERCENT, undefined);
    }
    this.dialogRef.close(true);
  }

  goBack() {
    this.dialogRef.close(false);
  }

  moveItemInArrayFromIndexToIndex = (array, fromIndex, toIndex) => {
    if (fromIndex === toIndex) {
      return array;
    }

    const newArray = [...array];

    [newArray[fromIndex], newArray[toIndex]] = [newArray[toIndex], newArray[fromIndex]];

    return newArray;
  };
}
